import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { agentDashboardAPI, managerAPI, tasksAPI, meetingsAPIExtended, usersAPI, aiWorkforceAPI, gamificationAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { UniversalSearch } from '../components/UniversalSearch';
import { LeadershipAIWidget } from '../components/LeadershipAIWidget';
import { CalendarWidget } from '../components/CalendarWidget';
import { CustomerPortalActivityPanel } from '../components/CustomerPortalActivityPanel';
import AICoachingPanel from '../components/AICoachingPanel';
import { StreakBadge, LeaderboardWidget, GamificationWidget } from '../components/GamificationWidgets';
import { showBadgeEarnedToast, celebrateTaskCompletion } from '../components/BadgeNotification';
import { format, formatDistanceToNow, parseISO, isSameDay, isBefore, startOfDay } from 'date-fns';
import { toast } from 'sonner';
import {
  CheckSquare, Calendar, Clock, TrendingUp, Bell, AlertTriangle,
  Target, Users, Phone, Mail, DollarSign, RefreshCw, ChevronRight,
  Megaphone, Award, BarChart3, Search, Plus, Video, ExternalLink,
  Upload, Paperclip, Bot, User, Filter, AlertCircle, Briefcase,
  Building, HeartPulse, Settings, Sparkles, Flame, Trophy, ListTodo
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

// Department color mapping
const DEPARTMENT_COLORS = {
  crm: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', label: 'CRM' },
  marketing: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200', label: 'Marketing' },
  sales: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Sales' },
  finance: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', label: 'Finance' },
  service: { bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-200', label: 'Service' },
  hr: { bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-200', label: 'HR' },
  operations: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', label: 'Operations' },
  default: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200', label: 'General' }
};

const getDepartmentColor = (department) => {
  return DEPARTMENT_COLORS[department?.toLowerCase()] || DEPARTMENT_COLORS.default;
};

export const MyDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [managerAlerts, setManagerAlerts] = useState([]);
  const [teamStats, setTeamStats] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const isManager = user?.role === 'admin' || user?.role === 'marketing_manager';
  
  // Gamification state
  const [gamificationStats, setGamificationStats] = useState(null);
  const [newBadges, setNewBadges] = useState([]);
  
  // Team members and AI agents for task assignment
  const [teamMembers, setTeamMembers] = useState([]);
  const [aiAgents, setAiAgents] = useState([]);
  
  // Schedule filter state
  const [scheduleFilter, setScheduleFilter] = useState('all'); // all, tasks, meetings, overdue, completed
  
  // Meeting detail dialog state (Bug #2 Fix)
  const [meetingDetailOpen, setMeetingDetailOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [meetingDetailLoading, setMeetingDetailLoading] = useState(false);
  
  // Quick task creation state
  const [quickTaskOpen, setQuickTaskOpen] = useState(false);
  const [quickTaskForm, setQuickTaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: format(new Date(), 'yyyy-MM-dd'),
    assigned_to: '',
    department: 'crm',
    attachments: []
  });
  const [creatingTask, setCreatingTask] = useState(false);
  const fileInputRef = useRef(null);
  
  // Weekly performance calculation
  const weeklyPerformance = useMemo(() => {
    if (!dashboardData?.weekly_stats) return { score: 0, breakdown: {} };
    
    const stats = dashboardData.weekly_stats;
    // Calculate weighted score: tasks (40%), meetings (30%), calls (30%)
    const taskScore = Math.min(stats.tasks_completed * 10, 40);
    const meetingScore = Math.min(stats.meetings_attended * 15, 30);
    const callScore = Math.min(stats.calls_made * 5, 30);
    const totalScore = taskScore + meetingScore + callScore;
    
    return {
      score: totalScore,
      breakdown: {
        tasks: { value: stats.tasks_completed, score: taskScore, max: 40 },
        meetings: { value: stats.meetings_attended, score: meetingScore, max: 30 },
        calls: { value: stats.calls_made, score: callScore, max: 30 }
      }
    };
  }, [dashboardData?.weekly_stats]);

  const fetchCalendarEvents = useCallback(async () => {
    try {
      const [tasksRes, meetingsRes] = await Promise.all([
        tasksAPI.getAll(),
        meetingsAPIExtended.getUpcoming()
      ]);

      const allEvents = [];
      const today = startOfDay(new Date());
      
      // Process tasks
      const tasks = Array.isArray(tasksRes.data) ? tasksRes.data : tasksRes.data?.tasks || [];
      tasks.forEach(task => {
        if (task.due_date) {
          // Only use related_to_id as contact_id if related_to_type is 'contact'
          const effectiveContactId = task.contact_id || 
            (task.related_to_type === 'contact' ? task.related_to_id : null);
          
          // Check if overdue
          const taskDate = parseISO(task.due_date);
          const isOverdue = task.status !== 'completed' && isBefore(taskDate, today);
          
          allEvents.push({
            id: task.id,
            type: task.is_follow_up ? 'followup' : 'task',
            title: task.title,
            date: task.due_date,
            time: task.due_time || null,
            status: task.status,
            priority: task.priority,
            contact_name: task.contact_name || task.related_to_name,
            contact_id: effectiveContactId,
            department: task.department || task.category || 'crm',
            assigned_by: task.created_by_name || task.assigned_by_name,
            assigned_by_id: task.created_by || task.assigned_by,
            is_ai_assigned: task.source_automation_rule_id || task.created_by_ai || false,
            isOverdue
          });
        }
      });

      // Process meetings
      const meetings = Array.isArray(meetingsRes.data) ? meetingsRes.data : meetingsRes.data?.meetings || [];
      meetings.forEach(meeting => {
        if (meeting.scheduled_datetime || meeting.start_time) {
          const dateStr = meeting.local_datetime || meeting.scheduled_datetime || meeting.start_time;
          // Use local_time if available (timezone-aware), otherwise fallback to parsing
          const displayTime = meeting.local_time || format(parseISO(dateStr), 'h:mm a');
          allEvents.push({
            id: meeting.id,
            type: 'meeting',
            title: meeting.title || meeting.meeting_type_name || 'Meeting',
            date: dateStr.split('T')[0],
            time: displayTime,
            contact_name: meeting.customer_name || meeting.contact_name,
            contact_id: meeting.contact_id,
            department: 'crm',
            isOverdue: false,
            timezone: meeting.timezone
          });
        }
      });

      setCalendarEvents(allEvents);
    } catch (error) {
      console.error('Failed to fetch calendar events:', error);
    }
  }, []);
  
  // Fetch team members and AI agents for task assignment
  const fetchAssignees = useCallback(async () => {
    try {
      const [usersRes, aiRes] = await Promise.all([
        usersAPI.getTeam().catch(() => ({ data: [] })),
        aiWorkforceAPI.getAgents().catch(() => ({ data: { agents: [] } }))
      ]);
      
      setTeamMembers(usersRes.data || []);
      setAiAgents(aiRes.data?.agents || aiRes.data || []);
    } catch (error) {
      console.error('Failed to fetch assignees:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchCalendarEvents();
    fetchAssignees();
    fetchGamificationStats();
  }, [fetchCalendarEvents, fetchAssignees]);

  const fetchData = async () => {
    try {
      const dashRes = await agentDashboardAPI.get();
      setDashboardData(dashRes.data);

      if (isManager) {
        const [alertsRes, statsRes] = await Promise.all([
          managerAPI.getAlerts(false),
          managerAPI.getTeamStats()
        ]);
        setManagerAlerts(alertsRes.data);
        setTeamStats(statsRes.data);
      }
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchGamificationStats = async () => {
    try {
      const response = await gamificationAPI.getMyStats();
      setGamificationStats(response.data);
    } catch (error) {
      console.error('Failed to fetch gamification stats:', error);
    }
  };

  const handleMarkAlertRead = async (alertId) => {
    try {
      await managerAPI.markAlertRead(alertId);
      setManagerAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (error) {
      toast.error('Failed to dismiss alert');
    }
  };
  
  // Handle file attachment selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setQuickTaskForm(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
  };
  
  const removeAttachment = (index) => {
    setQuickTaskForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };
  
  // Quick task creation handler
  const handleCreateQuickTask = async () => {
    if (!quickTaskForm.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }
    
    setCreatingTask(true);
    try {
      // Create task with all fields
      const taskData = {
        title: quickTaskForm.title,
        description: quickTaskForm.description || '',
        priority: quickTaskForm.priority,
        due_date: quickTaskForm.due_date || null,
        status: 'pending',
        task_type: 'crm'
      };
      
      // Handle assignment - only include if actually assigned
      if (quickTaskForm.assigned_to && quickTaskForm.assigned_to !== 'self' && quickTaskForm.assigned_to !== '') {
        if (quickTaskForm.assigned_to.startsWith('ai_')) {
          // Assign to AI agent
          taskData.assigned_to = quickTaskForm.assigned_to.replace('ai_', '');
          taskData.assigned_to_type = 'ai_agent';
          // Find agent name
          const agent = aiAgents.find(a => a.id === taskData.assigned_to);
          if (agent) {
            taskData.assigned_to_name = agent.name;
          }
        } else {
          // Assign to user
          taskData.assigned_to = quickTaskForm.assigned_to;
          taskData.assigned_to_type = 'user';
          // Find user name
          const member = teamMembers.find(m => m.id === quickTaskForm.assigned_to);
          if (member) {
            taskData.assigned_to_name = `${member.first_name} ${member.last_name}`;
          }
        }
      }
      
      await tasksAPI.create(taskData);
      
      // Upload attachments if any
      // Note: File attachments would need a separate upload endpoint for tasks
      // For now, we skip attachment upload since the API doesn't support it directly
      if (quickTaskForm.attachments.length > 0) {
        console.log('File attachments for tasks not yet implemented');
      }
      
      toast.success('Task created successfully');
      setQuickTaskOpen(false);
      setQuickTaskForm({
        title: '',
        description: '',
        priority: 'medium',
        due_date: format(new Date(), 'yyyy-MM-dd'),
        assigned_to: '',
        department: 'crm',
        attachments: []
      });
      fetchData(); // Refresh dashboard
      fetchCalendarEvents(); // Refresh calendar events
    } catch (error) {
      console.error('Failed to create task:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to create task');
    } finally {
      setCreatingTask(false);
    }
  };
  
  // Get filtered and sorted schedule (overdue first, then by filter)
  const filteredSchedule = useMemo(() => {
    const today = new Date();
    
    // Filter events based on selected filter
    let events = calendarEvents.filter(event => {
      try {
        const eventDate = parseISO(event.date);
        const isToday = isSameDay(eventDate, today);
        const isOverdue = event.isOverdue;
        const isCompleted = event.status === 'completed';
        const isTask = event.type === 'task' || event.type === 'followup';
        const isMeeting = event.type === 'meeting';
        
        switch (scheduleFilter) {
          case 'overdue':
            return isOverdue && !isCompleted;
          case 'completed':
            return isCompleted;
          case 'tasks':
            return isTask && (isToday || isOverdue || isCompleted);
          case 'meetings':
            return isMeeting && isToday;
          case 'all':
          default:
            // Show today's items + overdue + recently completed (last 7 days)
            if (isCompleted) {
              const sevenDaysAgo = new Date();
              sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
              return eventDate >= sevenDaysAgo;
            }
            return isToday || isOverdue;
        }
      } catch {
        return false;
      }
    });
    
    // Sort: overdue first, then pending, then completed, then by time
    events.sort((a, b) => {
      const aCompleted = a.status === 'completed';
      const bCompleted = b.status === 'completed';
      
      // Completed items last (unless in completed filter)
      if (scheduleFilter !== 'completed') {
        if (aCompleted && !bCompleted) return 1;
        if (!aCompleted && bCompleted) return -1;
      }
      
      // Overdue items first
      if (a.isOverdue && !b.isOverdue) return -1;
      if (!a.isOverdue && b.isOverdue) return 1;
      
      // Then by date (most recent first for completed)
      if (aCompleted && bCompleted) {
        return b.date.localeCompare(a.date);
      }
      
      // Then by time
      if (a.time && b.time) {
        return a.time.localeCompare(b.time);
      }
      return 0;
    });
    
    return events;
  }, [calendarEvents, scheduleFilter]);
  
  // Count overdue items
  const overdueCount = useMemo(() => {
    return calendarEvents.filter(e => e.isOverdue && e.status !== 'completed').length;
  }, [calendarEvents]);
  
  // Count completed items (this week)
  const completedCount = useMemo(() => {
    return calendarEvents.filter(e => e.status === 'completed').length;
  }, [calendarEvents]);
  
  // Task detail dialog state
  const [taskDetailOpen, setTaskDetailOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetailLoading, setTaskDetailLoading] = useState(false);
  
  // Handle event click - open task detail dialog directly instead of navigating
  const handleEventClick = useCallback(async (event) => {
    if (event.type === 'task' || event.type === 'followup') {
      // Fetch full task details and show in dialog
      setTaskDetailLoading(true);
      setTaskDetailOpen(true);
      try {
        const response = await tasksAPI.getById(event.id);
        setSelectedTask(response.data);
      } catch (error) {
        console.error('Failed to fetch task details:', error);
        toast.error('Failed to load task details');
        setTaskDetailOpen(false);
      } finally {
        setTaskDetailLoading(false);
      }
    } else if (event.type === 'meeting') {
      // Bug #2 Fix: Show meeting details in a dialog instead of navigating to non-existent page
      setMeetingDetailLoading(true);
      setMeetingDetailOpen(true);
      try {
        const response = await meetingsAPIExtended.getById(event.id);
        setSelectedMeeting(response.data);
      } catch (error) {
        console.error('Failed to fetch meeting details:', error);
        // If API call fails, use the event data we already have
        setSelectedMeeting({
          id: event.id,
          title: event.title,
          scheduled_datetime: event.date + 'T' + (event.time || '09:00'),
          meeting_type: event.meeting_type || 'call',
          contact_name: event.contact_name,
          contact_id: event.contact_id,
          timezone: event.timezone
        });
      } finally {
        setMeetingDetailLoading(false);
      }
    }
  }, [navigate]);
  
  // Track completing tasks for animation
  const [completingTaskId, setCompletingTaskId] = useState(null);
  
  // Handle task completion toggle directly from dashboard with optimistic UI
  const handleToggleTaskComplete = useCallback(async (event) => {
    const newStatus = event.status === 'completed' ? 'pending' : 'completed';
    const previousStatus = event.status;
    const previousBadgeCount = gamificationStats?.badges_earned?.length || 0;
    
    // Set completing state for animation
    if (newStatus === 'completed') {
      setCompletingTaskId(event.id);
      // Fire small confetti for task completion
      celebrateTaskCompletion();
    }
    
    // Optimistic update - update UI immediately
    setCalendarEvents(prev => prev.map(evt => 
      evt.id === event.id 
        ? { ...evt, status: newStatus, isOverdue: newStatus === 'completed' ? false : evt.isOverdue }
        : evt
    ));
    
    // Show immediate feedback
    toast.success(newStatus === 'completed' ? '✓ Task completed! +10 points' : 'Task reopened');
    
    try {
      await tasksAPI.update(event.id, { status: newStatus });
      
      // Refresh dashboard data to update weekly performance score
      fetchData();
      
      // Refresh gamification stats and check for new badges
      const statsResponse = await gamificationAPI.getMyStats();
      const newStats = statsResponse.data;
      setGamificationStats(newStats);
      
      // Check if user earned a new badge
      if (newStatus === 'completed' && newStats.badges_earned?.length > previousBadgeCount) {
        // Find the new badge(s) and show celebration
        const newBadges = newStats.badges_earned.slice(previousBadgeCount);
        newBadges.forEach((badge, idx) => {
          // Stagger multiple badge notifications
          setTimeout(() => {
            showBadgeEarnedToast(badge);
          }, idx * 1500);
        });
      }
      
      // Clear animation state after delay
      setTimeout(() => setCompletingTaskId(null), 1000);
    } catch (error) {
      console.error('Failed to update task:', error);
      // Revert on error
      setCalendarEvents(prev => prev.map(evt => 
        evt.id === event.id 
          ? { ...evt, status: previousStatus, isOverdue: previousStatus !== 'completed' ? evt.isOverdue : false }
          : evt
      ));
      setCompletingTaskId(null);
      toast.error('Failed to update task - reverted');
    }
  }, [fetchData, gamificationStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const { my_tasks, overdue_tasks, todays_bookings, my_deals, announcements, weekly_stats, overdue_count } = dashboardData || {};

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-dashboard-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold font-['Manrope']">
              Welcome back, {user?.first_name || 'Agent'}
            </h1>
            {/* Streak Badge */}
            {gamificationStats?.current_streak > 0 && (
              <StreakBadge streak={gamificationStats.current_streak} />
            )}
          </div>
          <p className="text-muted-foreground mt-1">
            Here&apos;s your overview for today, {format(new Date(), 'EEEE, MMMM d')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Mini stats */}
          {gamificationStats && (
            <div className="hidden md:flex items-center gap-3 px-3 py-1.5 bg-slate-100 rounded-lg">
              <div className="flex items-center gap-1 text-sm">
                <Trophy className="h-4 w-4 text-amber-500" />
                <span className="font-medium">{gamificationStats.badges_count}</span>
              </div>
              <div className="w-px h-4 bg-slate-300" />
              <div className="flex items-center gap-1 text-sm">
                <TrendingUp className="h-4 w-4 text-emerald-500" />
                <span className="font-medium">{gamificationStats.total_points} pts</span>
              </div>
            </div>
          )}
          <UniversalSearch className="w-80" />
          <Button 
            onClick={() => setQuickTaskOpen(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="quick-task-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Task
          </Button>
          <Button variant="outline" onClick={() => { fetchData(); fetchCalendarEvents(); fetchGamificationStats(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Customer Portal Activity - unified staff view of portal submissions */}
      <CustomerPortalActivityPanel />

      {/* Alerts for Managers */}
      {isManager && managerAlerts.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-amber-800 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Team Alerts ({managerAlerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {managerAlerts.slice(0, 5).map(alert => (
                <div key={alert.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                  <div className="flex items-center gap-3">
                    <Badge variant={alert.severity === 'high' ? 'destructive' : 'secondary'}>
                      {alert.alert_type.replace('_', ' ')}
                    </Badge>
                    <span className="text-sm">{alert.message}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleMarkAlertRead(alert.id)}>
                    Dismiss
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leadership AI Widget */}
      <LeadershipAIWidget />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Tasks Completed</p>
                <p className="text-3xl font-bold">{weekly_stats?.tasks_completed || 0}</p>
                <p className="text-xs text-muted-foreground">This week</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckSquare className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Meetings Attended</p>
                <p className="text-3xl font-bold">{weekly_stats?.meetings_attended || 0}</p>
                <p className="text-xs text-muted-foreground">This week</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Calls Made</p>
                <p className="text-3xl font-bold">{weekly_stats?.calls_made || 0}</p>
                <p className="text-xs text-muted-foreground">This week</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Phone className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={overdue_count > 0 ? 'border-red-200 bg-red-50' : ''}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Overdue Tasks</p>
                <p className={`text-3xl font-bold ${overdue_count > 0 ? 'text-red-600' : ''}`}>
                  {overdue_count || 0}
                </p>
                <p className="text-xs text-muted-foreground">Need attention</p>
              </div>
              <div className={`h-12 w-12 rounded-full flex items-center justify-center ${overdue_count > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                <AlertTriangle className={`h-6 w-6 ${overdue_count > 0 ? 'text-red-600' : 'text-slate-400'}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weekly Performance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-amber-500" />
            Weekly Performance Score
          </CardTitle>
          <CardDescription>Your activity points this week (target: 100)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="text-4xl font-bold text-primary">{weeklyPerformance.score}</div>
              <div className="flex-1">
                <Progress value={weeklyPerformance.score} className="h-3" />
                <p className="text-sm text-muted-foreground mt-1">
                  {weeklyPerformance.score >= 100 ? '🎉 Target achieved!' : `${100 - weeklyPerformance.score} points to target`}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="p-2 bg-emerald-50 rounded text-center">
                <div className="font-bold text-emerald-700">{weeklyPerformance.breakdown.tasks?.value || 0}</div>
                <div className="text-muted-foreground">Tasks ({weeklyPerformance.breakdown.tasks?.score || 0}/{weeklyPerformance.breakdown.tasks?.max || 40})</div>
              </div>
              <div className="p-2 bg-blue-50 rounded text-center">
                <div className="font-bold text-blue-700">{weeklyPerformance.breakdown.meetings?.value || 0}</div>
                <div className="text-muted-foreground">Meetings ({weeklyPerformance.breakdown.meetings?.score || 0}/{weeklyPerformance.breakdown.meetings?.max || 30})</div>
              </div>
              <div className="p-2 bg-purple-50 rounded text-center">
                <div className="font-bold text-purple-700">{weeklyPerformance.breakdown.calls?.value || 0}</div>
                <div className="text-muted-foreground">Calls ({weeklyPerformance.breakdown.calls?.score || 0}/{weeklyPerformance.breakdown.calls?.max || 30})</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's Schedule - Shows both tasks and meetings with filters */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Today&apos;s Schedule
                {overdueCount > 0 && (
                  <Badge variant="destructive" className="ml-2">
                    {overdueCount} overdue
                  </Badge>
                )}
              </CardTitle>
              {/* Filter tabs */}
              <Tabs value={scheduleFilter} onValueChange={setScheduleFilter} className="w-auto">
                <TabsList className="h-8">
                  <TabsTrigger value="all" className="text-xs px-2 h-6">All</TabsTrigger>
                  <TabsTrigger value="overdue" className="text-xs px-2 h-6">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Overdue {overdueCount > 0 && `(${overdueCount})`}
                  </TabsTrigger>
                  <TabsTrigger value="tasks" className="text-xs px-2 h-6">Tasks</TabsTrigger>
                  <TabsTrigger value="meetings" className="text-xs px-2 h-6">Meetings</TabsTrigger>
                  <TabsTrigger value="completed" className="text-xs px-2 h-6">
                    <CheckSquare className="h-3 w-3 mr-1" />
                    Completed {completedCount > 0 && `(${completedCount})`}
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          <CardContent>
            {filteredSchedule.length > 0 ? (
              <ScrollArea className="h-[350px]">
                <div className="space-y-3 pr-2">
                  {filteredSchedule.map((event, idx) => {
                    const isTask = event.type === 'task' || event.type === 'followup';
                    const isMeeting = event.type === 'meeting';
                    const isCompleted = event.status === 'completed';
                    const isAnimating = completingTaskId === event.id;
                    const deptColor = getDepartmentColor(event.department);
                    
                    return (
                      <div 
                        key={event.id || idx} 
                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all duration-300 hover:shadow-sm relative ${
                          isAnimating ? 'scale-[0.98] bg-emerald-100 border-emerald-400 shadow-lg' :
                          isCompleted ? 'opacity-60 bg-emerald-50 border-emerald-200' :
                          event.isOverdue ? 'border-red-300 bg-red-50' : 
                          event.type === 'followup' ? 'border-amber-200 bg-amber-50' : 
                          `${deptColor.border} ${deptColor.bg}`
                        }`}
                        onClick={() => handleEventClick(event)}
                        data-testid={`schedule-item-${event.id}`}
                      >
                        {/* Checkbox for tasks */}
                        {isTask && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={isCompleted}
                              onCheckedChange={() => handleToggleTaskComplete(event)}
                              className={`h-5 w-5 transition-all ${isCompleted ? 'bg-emerald-500 border-emerald-500' : ''} ${isAnimating ? 'scale-110' : ''}`}
                              data-testid={`complete-task-${event.id}`}
                            />
                          </div>
                        )}
                        
                        {/* Icon */}
                        <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 transition-all ${
                          isAnimating ? 'bg-emerald-200 scale-110' :
                          isCompleted ? 'bg-emerald-100' :
                          event.isOverdue ? 'bg-red-100' :
                          isMeeting ? 'bg-purple-100' : 
                          event.type === 'followup' ? 'bg-amber-100' : 
                          deptColor.bg
                        }`}>
                          {isCompleted ? (
                            <CheckSquare className="h-4 w-4 text-emerald-600" />
                          ) : isMeeting ? (
                            <Video className="h-4 w-4 text-purple-600" />
                          ) : event.is_ai_assigned ? (
                            <Bot className="h-4 w-4 text-violet-600" />
                          ) : (
                            <CheckSquare className={`h-4 w-4 ${
                              event.isOverdue ? 'text-red-600' :
                              event.type === 'followup' ? 'text-amber-600' : 
                              deptColor.text
                            }`} />
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* Completed badge */}
                            {isCompleted && (
                              <Badge className="bg-emerald-500 text-white text-xs">
                                <Sparkles className="h-3 w-3 mr-1" />
                                DONE
                              </Badge>
                            )}
                            
                            {/* Department badge */}
                            {!isCompleted && (
                              <Badge variant="outline" className={`${deptColor.bg} ${deptColor.text} text-xs`}>
                                {deptColor.label}
                              </Badge>
                            )}
                            
                            {/* Type/Status badge */}
                            {!isCompleted && (
                              <Badge variant="outline" className={
                                event.isOverdue ? 'bg-red-100 text-red-700' :
                                isMeeting ? 'bg-purple-100 text-purple-700' : 
                                event.type === 'followup' ? 'bg-amber-100 text-amber-700' : 
                                'bg-blue-100 text-blue-700'
                              }>
                                {event.isOverdue ? 'OVERDUE' : isMeeting ? 'Meeting' : event.type === 'followup' ? 'Follow-up' : 'Task'}
                              </Badge>
                            )}
                            
                            {/* AI assigned indicator */}
                            {event.is_ai_assigned && !isCompleted && (
                              <Badge variant="outline" className="bg-violet-100 text-violet-700 text-xs">
                                <Bot className="h-3 w-3 mr-1" />
                                AI
                              </Badge>
                            )}
                            
                            <span className={`font-medium truncate ${isCompleted ? 'line-through text-muted-foreground' : ''}`}>
                              {event.title}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1 flex-wrap">
                            {event.time && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {event.time}
                              </span>
                            )}
                            {event.contact_name && (
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {event.contact_name}
                              </span>
                            )}
                            {/* Show who assigned the task */}
                            {event.assigned_by && (
                              <span className="flex items-center gap-1 text-xs">
                                {event.is_ai_assigned ? (
                                  <Bot className="h-3 w-3 text-violet-500" />
                                ) : (
                                  <User className="h-3 w-3" />
                                )}
                                <span className="text-muted-foreground">by {event.assigned_by}</span>
                              </span>
                            )}
                          </div>
                        </div>
                        <ExternalLink className="h-4 w-4 text-muted-foreground shrink-0" />
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>
                  {scheduleFilter === 'overdue' 
                    ? 'No overdue items - great work!' 
                    : 'No items scheduled for today'}
                </p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setQuickTaskOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add a Task
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Calendar Widget */}
        <CalendarWidget 
          events={calendarEvents}
          selectedDate={selectedCalendarDate}
          onDateSelect={setSelectedCalendarDate}
        />
      </div>

      {/* Announcements Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5" />
            Announcements
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[200px]">
            {announcements?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {announcements.map(ann => (
                  <div key={ann.id} className="border rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <h4 className="font-medium">{ann.title}</h4>
                      {ann.is_pinned && <Badge variant="outline">Pinned</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{ann.content}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {formatDistanceToNow(new Date(ann.created_at), { addSuffix: true })}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Megaphone className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No announcements</p>
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Tasks and Deals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* My Tasks */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CheckSquare className="h-5 w-5" />
                My Tasks
              </CardTitle>
              <CardDescription>Upcoming and overdue tasks</CardDescription>
            </div>
            <Link to="/tasks">
              <Button variant="ghost" size="sm">
                View All <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[250px]">
              {my_tasks?.length > 0 ? (
                <div className="space-y-2">
                  {overdue_tasks?.slice(0, 3).map(task => (
                    <div key={task.id} className="flex items-center gap-3 p-2 rounded-lg bg-red-50 border border-red-200">
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-red-700">{task.title}</p>
                        <p className="text-xs text-red-500">
                          Overdue: {format(new Date(task.due_date), 'MMM d')}
                        </p>
                      </div>
                    </div>
                  ))}
                  {my_tasks.map(task => (
                    <div key={task.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50">
                      <CheckSquare className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{task.title}</p>
                        <p className="text-xs text-muted-foreground">
                          Due: {task.due_date ? format(new Date(task.due_date), 'MMM d') : 'No due date'}
                        </p>
                      </div>
                      <Badge variant="outline" className="text-xs">{task.priority}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No pending tasks</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* My Deals */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Active Deals
              </CardTitle>
              <CardDescription>Your pipeline deals</CardDescription>
            </div>
            <Link to="/deals">
              <Button variant="ghost" size="sm">
                View All <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[250px]">
              {my_deals?.length > 0 ? (
                <div className="space-y-2">
                  {my_deals.map(deal => (
                    <div key={deal.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{deal.title}</p>
                        <p className="text-xs text-muted-foreground">{deal.status}</p>
                      </div>
                      <span className="font-semibold text-emerald-600">
                        ${deal.value?.toLocaleString() || 0}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <DollarSign className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No active deals</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Team Stats for Managers */}
      {isManager && teamStats.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Team Performance
            </CardTitle>
            <CardDescription>Weekly stats for your team members</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {teamStats.map(stat => (
                <div key={stat.agent?.id} className="p-4 border rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center font-medium">
                      {stat.agent?.first_name?.[0]}{stat.agent?.last_name?.[0]}
                    </div>
                    <div>
                      <p className="font-medium">{stat.agent?.first_name} {stat.agent?.last_name}</p>
                      <p className="text-xs text-muted-foreground">{stat.agent?.role}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Tasks:</span>
                      <span className="ml-1 font-medium">{stat.tasks_completed}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Meetings:</span>
                      <span className="ml-1 font-medium">{stat.meetings_attended}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Points:</span>
                      <span className="ml-1 font-medium">{stat.total_points}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Overdue:</span>
                      <span className={`ml-1 font-medium ${stat.overdue_tasks > 0 ? 'text-red-600' : ''}`}>
                        {stat.overdue_tasks}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Gamification Section - Leaderboard and Achievements */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <LeaderboardWidget period="weekly" limit={5} />
        <GamificationWidget />
      </div>

      {/* AI Coach Section - Personalized recommendations */}
      <div className="mt-6">
        <AICoachingPanel />
      </div>

      {/* Task Detail Dialog - Opens directly on dashboard without navigation */}
      <Dialog open={taskDetailOpen} onOpenChange={setTaskDetailOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckSquare className="h-5 w-5 text-blue-600" />
              Task Details
            </DialogTitle>
          </DialogHeader>
          {taskDetailLoading ? (
            <div className="space-y-4 py-4">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : selectedTask ? (
            <div className="space-y-4 py-4">
              {/* Task Title & Status */}
              <div className="flex items-start justify-between gap-3">
                <h3 className={`text-lg font-semibold ${selectedTask.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                  {selectedTask.title}
                </h3>
                <Badge 
                  className={selectedTask.status === 'completed' ? 'bg-emerald-500' : 'bg-blue-500'}
                >
                  {selectedTask.status === 'completed' ? 'Completed' : selectedTask.status}
                </Badge>
              </div>
              
              {/* Task Metadata */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>Due: {selectedTask.due_date ? format(parseISO(selectedTask.due_date), 'MMM d, yyyy') : 'No date'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <AlertTriangle className={`h-4 w-4 ${selectedTask.priority === 'high' || selectedTask.priority === 'urgent' ? 'text-red-500' : 'text-muted-foreground'}`} />
                  <span className="capitalize">Priority: {selectedTask.priority || 'Medium'}</span>
                </div>
                {selectedTask.contact_name && (
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedTask.contact_name}</span>
                  </div>
                )}
                {selectedTask.department && (
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-muted-foreground" />
                    <span className="capitalize">{selectedTask.department}</span>
                  </div>
                )}
              </div>
              
              {/* Description */}
              {selectedTask.description && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <Label className="text-xs text-muted-foreground">Description</Label>
                  <p className="mt-1 text-sm whitespace-pre-wrap">{selectedTask.description}</p>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex items-center gap-2 pt-2">
                <Button
                  variant={selectedTask.status === 'completed' ? 'outline' : 'default'}
                  className={selectedTask.status === 'completed' ? '' : 'bg-emerald-600 hover:bg-emerald-700'}
                  onClick={async () => {
                    const newStatus = selectedTask.status === 'completed' ? 'pending' : 'completed';
                    try {
                      await tasksAPI.update(selectedTask.id, { status: newStatus });
                      setSelectedTask(prev => ({ ...prev, status: newStatus }));
                      // Update calendar events
                      setCalendarEvents(prev => prev.map(evt => 
                        evt.id === selectedTask.id 
                          ? { ...evt, status: newStatus, isOverdue: newStatus === 'completed' ? false : evt.isOverdue }
                          : evt
                      ));
                      fetchData(); // Refresh dashboard stats
                      toast.success(newStatus === 'completed' ? '✓ Task completed!' : 'Task reopened');
                    } catch (error) {
                      toast.error('Failed to update task');
                    }
                  }}
                  data-testid="task-detail-complete-btn"
                >
                  {selectedTask.status === 'completed' ? 'Reopen Task' : (
                    <>
                      <CheckSquare className="h-4 w-4 mr-2" />
                      Mark Complete
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setTaskDetailOpen(false);
                    navigate(`/tasks?taskId=${selectedTask.id}`);
                  }}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open Full View
                </Button>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              Task not found
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Task Creation Dialog */}
      <Dialog open={quickTaskOpen} onOpenChange={setQuickTaskOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-blue-600" />
              Add Quick Task
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="task-title">Task Title *</Label>
              <Input
                id="task-title"
                placeholder="Enter task title..."
                value={quickTaskForm.title}
                onChange={(e) => setQuickTaskForm(prev => ({ ...prev, title: e.target.value }))}
                data-testid="quick-task-title"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="task-desc">Description</Label>
              <Textarea
                id="task-desc"
                placeholder="Add details..."
                rows={2}
                value={quickTaskForm.description}
                onChange={(e) => setQuickTaskForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={quickTaskForm.due_date}
                  onChange={(e) => setQuickTaskForm(prev => ({ ...prev, due_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={quickTaskForm.priority}
                  onValueChange={(value) => setQuickTaskForm(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Department */}
              <div className="space-y-2">
                <Label>Department</Label>
                <Select
                  value={quickTaskForm.department}
                  onValueChange={(value) => setQuickTaskForm(prev => ({ ...prev, department: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="crm">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500" />
                        CRM
                      </span>
                    </SelectItem>
                    <SelectItem value="marketing">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-purple-500" />
                        Marketing
                      </span>
                    </SelectItem>
                    <SelectItem value="sales">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-emerald-500" />
                        Sales
                      </span>
                    </SelectItem>
                    <SelectItem value="finance">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-amber-500" />
                        Finance
                      </span>
                    </SelectItem>
                    <SelectItem value="service">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-cyan-500" />
                        Service
                      </span>
                    </SelectItem>
                    <SelectItem value="hr">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-pink-500" />
                        HR
                      </span>
                    </SelectItem>
                    <SelectItem value="operations">
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-orange-500" />
                        Operations
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Assign To */}
              <div className="space-y-2">
                <Label>Assign To</Label>
                <Select
                  value={quickTaskForm.assigned_to}
                  onValueChange={(value) => setQuickTaskForm(prev => ({ ...prev, assigned_to: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Self (default)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">
                      <span className="flex items-center gap-2">
                        <User className="h-3 w-3" />
                        Myself
                      </span>
                    </SelectItem>
                    
                    {/* Team Members */}
                    {teamMembers.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">Team Members</div>
                        {teamMembers.slice(0, 10).map(member => (
                          <SelectItem key={member.id} value={member.id}>
                            <span className="flex items-center gap-2">
                              <User className="h-3 w-3" />
                              {member.first_name} {member.last_name}
                            </span>
                          </SelectItem>
                        ))}
                      </>
                    )}
                    
                    {/* AI Agents */}
                    {aiAgents.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">AI Agents</div>
                        {aiAgents.slice(0, 5).map(agent => (
                          <SelectItem key={`ai_${agent.id}`} value={`ai_${agent.id}`}>
                            <span className="flex items-center gap-2">
                              <Bot className="h-3 w-3 text-violet-500" />
                              {agent.name}
                            </span>
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Attachments */}
            <div className="space-y-2">
              <Label>Attachments</Label>
              <div className="border-2 border-dashed rounded-lg p-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full"
                >
                  <Paperclip className="h-4 w-4 mr-2" />
                  Add Attachments
                </Button>
                
                {quickTaskForm.attachments.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {quickTaskForm.attachments.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs p-1 bg-slate-50 rounded">
                        <span className="truncate">{file.name}</span>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          className="h-5 w-5 p-0"
                          onClick={() => removeAttachment(idx)}
                        >
                          ×
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickTaskOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateQuickTask} 
              disabled={creatingTask || !quickTaskForm.title.trim()}
              data-testid="quick-task-submit"
            >
              {creatingTask ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Meeting Detail Dialog (Bug #2 Fix) */}
      <Dialog open={meetingDetailOpen} onOpenChange={setMeetingDetailOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="h-5 w-5 text-purple-600" />
              Meeting Details
            </DialogTitle>
          </DialogHeader>
          {meetingDetailLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-purple-600" />
            </div>
          ) : selectedMeeting ? (
            <div className="space-y-4" data-testid="meeting-detail-content">
              <div>
                <h3 className="text-lg font-semibold">{selectedMeeting.title}</h3>
                <Badge variant="outline" className="mt-1">
                  {selectedMeeting.meeting_type || 'Meeting'}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Date & Time</Label>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">
                      {selectedMeeting.scheduled_datetime 
                        ? format(parseISO(selectedMeeting.scheduled_datetime), 'MMM d, yyyy h:mm a')
                        : 'Not scheduled'}
                    </span>
                  </div>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Duration</Label>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{selectedMeeting.duration_minutes || 30} minutes</span>
                  </div>
                </div>
              </div>

              {selectedMeeting.contact_name && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Attendee</Label>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{selectedMeeting.contact_name}</span>
                    {selectedMeeting.contact_id && (
                      <Link 
                        to={`/contacts/${selectedMeeting.contact_id}`}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        View Contact
                      </Link>
                    )}
                  </div>
                </div>
              )}

              {selectedMeeting.customer_email && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Email</Label>
                  <p className="text-sm">{selectedMeeting.customer_email}</p>
                </div>
              )}

              {selectedMeeting.description && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Description</Label>
                  <p className="text-sm text-muted-foreground">{selectedMeeting.description}</p>
                </div>
              )}

              {selectedMeeting.meeting_link && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Meeting Link</Label>
                  <a 
                    href={selectedMeeting.meeting_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Join Meeting
                  </a>
                </div>
              )}

              {selectedMeeting.timezone && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">Timezone</Label>
                  <p className="text-sm text-muted-foreground">{selectedMeeting.timezone}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4">No meeting details available</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMeetingDetailOpen(false)}>
              Close
            </Button>
            {selectedMeeting?.contact_id && (
              <Button 
                onClick={() => {
                  setMeetingDetailOpen(false);
                  navigate(`/contacts/${selectedMeeting.contact_id}`);
                }}
              >
                View Contact
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bug #16 Fix: Floating Action Button for Quick Add */}
      <div className="fixed bottom-6 right-6 z-50">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              size="lg" 
              className="h-14 w-14 rounded-full shadow-lg bg-gradient-to-br from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 transition-all hover:scale-105"
              data-testid="fab-quick-add"
            >
              <Plus className="h-6 w-6" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 mb-2">
            <DropdownMenuItem onClick={() => setQuickTaskOpen(true)} data-testid="fab-add-task">
              <ListTodo className="h-4 w-4 mr-2" />
              New Task
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/contacts?action=new')} data-testid="fab-add-contact">
              <User className="h-4 w-4 mr-2" />
              New Contact
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/deals?action=new')} data-testid="fab-add-deal">
              <Target className="h-4 w-4 mr-2" />
              New Deal
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/calendar?action=meeting')} data-testid="fab-add-meeting">
              <Calendar className="h-4 w-4 mr-2" />
              Schedule Meeting
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default MyDashboardPage;
